import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import type {
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTableOperatorFilterMetaData,
} from 'primevue';
import type { ColumnField, ColumnFilterType } from './useColumnSelector';

/** A `global` entry (for the search field) plus one entry per filterable column. */
export type ColumnFilters = {
  global: DataTableFilterMetaData;
  [field: string]: DataTableFilterMetaData | DataTableOperatorFilterMetaData;
};

interface FilterTypeDefaults {
  matchMode: string;
  withOperator: boolean;
}

function defaultsFor(type: ColumnFilterType): FilterTypeDefaults {
  switch (type) {
    case 'numeric':
      return { matchMode: FilterMatchMode.EQUALS, withOperator: true };
    case 'select':
      return { matchMode: FilterMatchMode.IN, withOperator: false };
    case 'boolean':
      return { matchMode: FilterMatchMode.EQUALS, withOperator: false };
    case 'text':
    default:
      return { matchMode: FilterMatchMode.CONTAINS, withOperator: true };
  }
}

function isEmptyValue(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Builds and manages PrimeVue DataTable per-column filters from the same `ColumnField`
 * definitions used by `useColumnSelector`. Columns with a `filterType` get a filter entry;
 * a `global` entry is always included for the search field.
 */
export function useColumnFilters(options: {
  columns: MaybeRefOrGetter<ColumnField[]>;
  totalCount: MaybeRefOrGetter<number>;
}) {
  const filters = ref<ColumnFilters>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const filteredCount = ref(0);

  function buildFilters() {
    const next: ColumnFilters = {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    for (const column of toValue(options.columns)) {
      if (!column.filterType) continue;

      const key = column.filterField ?? column.field;
      const defaults = defaultsFor(column.filterType);
      const matchMode = column.filterMatchMode ?? defaults.matchMode;
      const withOperator = column.showFilterOperator ?? defaults.withOperator;

      next[key] = withOperator
        ? { operator: FilterOperator.AND, constraints: [{ value: null, matchMode }] }
        : { value: null, matchMode };
    }

    filters.value = next;
    filteredCount.value = toValue(options.totalCount);
  }

  buildFilters();

  const hasActiveFilters = computed(() =>
    Object.values(filters.value).some((meta) => {
      if (!meta) return false;
      if ('constraints' in meta && Array.isArray(meta.constraints)) {
        return meta.constraints.some((constraint) => !isEmptyValue(constraint?.value));
      }
      return !isEmptyValue((meta as DataTableFilterMetaData).value);
    }),
  );

  function clearFilters() {
    buildFilters();
  }

  function onFilter(event: DataTableFilterEvent) {
    filteredCount.value = event.filteredValue?.length ?? toValue(options.totalCount);
  }

  return { filters, filteredCount, hasActiveFilters, clearFilters, onFilter };
}
